import React from 'react';
import { Shield, AlertTriangle, Activity, LockKeyhole } from 'lucide-react';
import { EstadisticaSeguridad } from '../../types';

interface SecurityStatsProps {
  stats: EstadisticaSeguridad;
}

const SecurityStats: React.FC<SecurityStatsProps> = ({ stats }) => {
  // Función para determinar el color basado en el nivel de riesgo
  const getRiskColor = (risk: number) => {
    if (risk < 30) return 'text-cyber-safe';
    if (risk < 60) return 'text-cyber-warning';
    return 'text-cyber-alert';
  };

  const getRiskBg = (risk: number) => {
    if (risk < 30) return 'bg-cyber-safe';
    if (risk < 60) return 'bg-cyber-warning';
    return 'bg-cyber-alert';
  };

  return (
    <div className="bg-cyber-dark-blue bg-opacity-80 backdrop-blur-md p-4 rounded-lg border border-cyber-blue/30">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <Shield className="h-5 w-5 mr-2 text-cyber-blue" />
        Estado de Seguridad
      </h3>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-cyber-gray/20 p-3 rounded-lg border border-cyber-gray/30">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs text-cyber-light">Intentos Bloqueados</p>
              <p className="text-xl font-bold text-white">{stats.intentosBloqueo.toLocaleString()}</p>
            </div>
            <LockKeyhole className="h-6 w-6 text-cyber-blue" />
          </div>
        </div>
        
        <div className="bg-cyber-gray/20 p-3 rounded-lg border border-cyber-gray/30">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs text-cyber-light">Vulnerabilidades</p>
              <p className="text-xl font-bold text-white">{stats.vulnerabilidadesDetectadas}</p>
            </div>
            <AlertTriangle className="h-6 w-6 text-cyber-warning" />
          </div>
        </div>
        
        <div className="bg-cyber-gray/20 p-3 rounded-lg border border-cyber-gray/30">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs text-cyber-light">Amenazas Activas</p>
              <p className="text-xl font-bold text-white">{stats.amenazasActivas}</p>
            </div>
            <Activity className={`h-6 w-6 ${stats.amenazasActivas > 5 ? 'text-cyber-alert' : 'text-cyber-warning'}`} />
          </div>
        </div>
        
        <div className="bg-cyber-gray/20 p-3 rounded-lg border border-cyber-gray/30">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs text-cyber-light">Última Actualización</p>
              <p className="text-sm font-medium text-white">
                {stats.ultimaActualizacion.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div className="w-3 h-3 bg-cyber-safe rounded-full animate-pulse mt-1"></div>
          </div>
        </div>
      </div>
      
      {/* Indicador de nivel de riesgo */}
      <div className="mb-2">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-cyber-light">Nivel de Riesgo</span>
          <span className={`text-sm font-medium ${getRiskColor(stats.nivelRiesgo)}`}>
            {stats.nivelRiesgo}%
          </span>
        </div>
        <div className="h-2 bg-cyber-gray/30 rounded-full overflow-hidden">
          <div 
            className={`h-full ${getRiskBg(stats.nivelRiesgo)} transition-all duration-500 ease-in-out`}
            style={{ width: `${stats.nivelRiesgo}%` }}
          ></div>
        </div>
      </div>
      
      {/* Leyenda de estado */}
      <div className="mt-4 text-xs text-cyber-light">
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <span className="inline-block w-2 h-2 bg-cyber-safe rounded-full mr-1"></span>
            Bajo riesgo (&lt;30%)
          </span>
          <span className="flex items-center">
            <span className="inline-block w-2 h-2 bg-cyber-warning rounded-full mr-1"></span>
            Precaución (30-60%)
          </span>
          <span className="flex items-center">
            <span className="inline-block w-2 h-2 bg-cyber-alert rounded-full mr-1"></span>
            Alto riesgo (&gt;60%)
          </span>
        </div>
      </div>
    </div>
  );
};

export default SecurityStats;